import React, { useMemo, useState } from 'react';
import Card, { CardContent } from '../../../components/ui/Card';
import { downloadCSV, generateReport, ReportFormat, ReportType } from '../../../services/analyticsReportsService';

const reportTypes: { value: ReportType; label: string; description: string }[] = [
  { value: 'daily-performance', label: 'Daily Performance', description: 'Daily revenue, payments, and posts' },
  { value: 'weekly-performance', label: 'Weekly Performance', description: 'Weekly aggregates of daily performance' },
  { value: 'patron-acquisition', label: 'Patron Acquisition', description: 'New members per day' },
];

const AnalyticsReportsPage: React.FC = () => {
  const [type, setType] = useState<ReportType>('daily-performance');
  const [format, setFormat] = useState<ReportFormat>('json');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any[] | null>(null);

  const canDownload = useMemo(() => !!type && !!format, [type, format]);

  async function handleGenerate() {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      if (format === 'csv') {
        const blob = await downloadCSV({ type, format, startDate: startDate || undefined, endDate: endDate || undefined });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        const res = await generateReport({ type, format: 'json', startDate: startDate || undefined, endDate: endDate || undefined });
        if (!res.success) throw new Error('Report failed');
        setResult(res.data);
      }
    } catch (e:any) {
      setError(e.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Analytics Reports</h1>
          <p className="text-gray-600">Generate downloadable analytics reports</p>
        </div>
      </div>

      <Card className="mb-6">
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
              <select value={type} onChange={e => setType(e.target.value as ReportType)} className="w-full border rounded-md p-2">
                {reportTypes.map(rt => (
                  <option key={rt.value} value={rt.value}>{rt.label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">{reportTypes.find(r => r.value === type)?.description}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
              <select value={format} onChange={e => setFormat(e.target.value as ReportFormat)} className="w-full border rounded-md p-2">
                <option value="json">JSON</option>
                <option value="csv">CSV</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full border rounded-md p-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full border rounded-md p-2" />
            </div>
          </div>
          <div className="flex justify-end">
            <button disabled={!canDownload || loading} onClick={handleGenerate} className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-60">
              {loading ? 'Generating...' : 'Generate'}
            </button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="p-3 bg-red-50 text-red-700 rounded mb-4">{error}</div>
      )}

      {result && (
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold mb-4">Preview (first 20 rows)</h2>
            <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto max-h-96">{JSON.stringify(result.slice(0, 20), null, 2)}</pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsReportsPage;
